import { bridge } from "@multiweb/reactCore/bridge";
import { LOC_NEW_SET } from "../locKeys";
import { parseStringToBoolean } from "../utils";
import { Activity, ActivityType, IOTransitionType, Point, Position, Set, Transition, Workflow } from "../workflowModel";
import { Action, CallMethod, CallScript, IncrementDateTime, IncrementNumber, SendWFMessage, SetObjectAttribute, SetValue } from "../workflowModel/Actions";
import { DefinitionParticipant, Owner, Participant, Role, System, User } from "../workflowModel/Participants";
import { AttributeDataChange, Menu, NewObject, Text, Trigger, WfMessage } from "../workflowModel/Triggers";
import {
    CONST_ACTION_COMMENT_AT,
    CONST_ACTION_CONDITION_AT,
    CONST_ACTIONS_EL,
    CONST_ACTIVITY_CONDITION_AT,
    CONST_ACTIVITY_DESCRIPTION_AT,
    CONST_ACTIVITY_EL,
    CONST_ACTIVITY_ID_AT,
    CONST_ACTIVITY_INHERIT_PARTICIPANTS_AT,
    CONST_ACTIVITY_INPUT_TRANS_TYPE_AT,
    CONST_ACTIVITY_KILL_CHILDREN_AT,
    CONST_ACTIVITY_NAME_AT,
    CONST_ACTIVITY_ORDER_AT,
    CONST_ACTIVITY_RUN_CONDITION_AT,
    CONST_ACTIVITY_SHOW_IN_WF_HISTORY_AT,
    CONST_ACTIVITY_TYPE_AT,
    CONST_ATTRIBUTE_DATA_CHANGE_ATTR_NAME_AT,
    CONST_ATTRIBUTE_DATA_CHANGE_CONDITION_AT,
    CONST_ATTRIBUTE_DATA_CHANGE_EL,
    CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT,
    CONST_CALL_METHOD_EL,
    CONST_CALL_METHOD_INSERT_ACTUAL_OBJECT_AT,
    CONST_CALL_METHOD_INSERT_PARAM_FORM_OBJECT_AT,
    CONST_CALL_METHOD_METHOD_NAME_AT,
    CONST_CALL_METHOD_STATIC_AT,
    CONST_CALL_SCRIPT_EL,
    CONST_DEFINITION_PARTICIPANT_CONDITION_AT,
    CONST_DEFINITION_PARTICIPANT_EL,
    CONST_DEFINITION_PARTICIPANT_NAME_AT,
    CONST_DEFINITION_PARTICIPANT_ROLE_NAME_AT,
    CONST_DEFINITION_PARTICIPANTS_EL,
    CONST_DRAW_EL,
    CONST_DRAW_SET_AT,
    CONST_INCREMENT_DATE_TIME_ATTR_NAME_AT,
    CONST_INCREMENT_DATE_TIME_EL,
    CONST_INCREMENT_DATE_TIME_PRECISION_AT,
    CONST_INCREMENT_DATE_TIME_VALUE_AT,
    CONST_INCREMENT_NUMBER_ATTR_NAME_AT,
    CONST_INCREMENT_NUMBER_EL,
    CONST_INCREMENT_NUMBER_VALUE_AT,
    CONST_MENU_CAPTIONS_EL,
    CONST_MENU_CHOOSE_FROM_USERS_DEFAULT,
    CONST_MENU_CODE_NAME,
    CONST_MENU_CONFIRM_MESSAGES_EL,
    CONST_MENU_EL,
    CONST_MENU_ICON_NAME,
    CONST_MENU_IDENTIFIER_AT,
    CONST_MENU_LANG_AT,
    CONST_MENU_LINK_ATTR_FOR_SEL_OBJ_AT,
    CONST_MENU_MASTER_NODE_AT,
    CONST_MENU_MULTI_ACTION_ENABLE_AT,
    CONST_MENU_PARAM_FORM_NAME_AT,
    CONST_MENU_PARTICIPANTS_SELECTING_AT,
    CONST_MENU_SET_VALUES_AT,
    CONST_MENU_TEXT_AT,
    CONST_MENU_VALIDATE_POST_REQ_AT,
    CONST_MENU_VALIDATE_RECOMMENDED_AT,
    CONST_MESSAGE_RECIPIENT_EL,
    CONST_NEW_OBJECT_EL,
    CONST_OWNER_EL,
    CONST_PARTICIPANTS_EL,
    CONST_POINT_X_AT,
    CONST_POINT_Y_AT,
    CONST_POSITION_EL,
    CONST_POSITION_HEIGHT_AT,
    CONST_POSITION_SET_AT,
    CONST_POSITION_WIDTH_AT,
    CONST_POSITION_X_AT,
    CONST_POSITION_Y_AT,
    CONST_ROLE_DB_ID_AT,
    CONST_ROLE_EL,
    CONST_ROLE_NAME_AT,
    CONST_SEND_CALENDAR_EL,
    CONST_SEND_MAIL_EL,
    CONST_SEND_SMS_EL,
    CONST_SEND_WF_MESSAGE_AFTER_SAVE_AT,
    CONST_SEND_WF_MESSAGE_EL,
    CONST_SEND_WF_MESSAGE_MESSAGE_NAME_AT,
    CONST_SEND_WF_MESSAGE_OBJECTS_CONDITION_AT,
    CONST_SEND_WF_MESSAGE_OBJECTS_CONDITION_PARAMS_AT,
    CONST_SEND_WF_MESSAGE_TARGET_OBJECT_NAME_AT,
    CONST_SET_ATTRIBUTE_ATTR_NAME_AT,
    CONST_SET_ATTRIBUTE_EL,
    CONST_SET_DEFINITIONS_EL,
    CONST_SET_DESCRIPTION_AT,
    CONST_SET_NAME_AT,
    CONST_SET_OBJECT_ATTRIBUTE_EL,
    CONST_SET_OBJECT_ATTRIBUTE_MASTER_ATTR_NAME_AT,
    CONST_SET_OBJECT_ATTRIBUTE_MASTER_CLASS_NAME_AT,
    CONST_SET_VALUE_EL,
    CONST_SET_VALUE_NAME_AT,
    CONST_SYSTEM_EL,
    CONST_TRANSITION_CONDITION_AT,
    CONST_TRANSITION_ID_AT,
    CONST_TRANSITION_NAME_AT,
    CONST_TRANSITION_TO_AT,
    CONST_TRANSITIONS_EL,
    CONST_TRIGGERS_EL,
    CONST_USER_DB_ID_AT,
    CONST_USER_EL,
    CONST_USER_NAME_AT,
    CONST_WFMESSAGE_DEACTIVATING_AT,
    CONST_WFMESSAGE_EL,
    CONST_WFMESSAGE_MESSAGE_NAME_AT,
    CONST_WORKFLOW_CLASS_NAME_AT,
    CONST_WORKFLOW_DEFINITION_VERSION_AT,
    CONST_WORKFLOW_IDENTIFIER_AT,
    CONST_WORKFLOW_VERSION_AT,
} from "./constants";
import { manageSendMailXmlElement, manageSendSmsXmlElement, manageSetAttributeXmlElement } from "./oldSilverlightFunctions";
import { unescapeXmlString } from "./utils";

const { loadLocalizationItems, generateUUID } = bridge;

export const xmlToWorkflow = async (xmlString: string): Promise<Workflow> => {
    if (xmlString == null || xmlString.length < 0)
        throw new Error("Provided string of workflow definition is empty.");

    const parser = new DOMParser();
    const formDoc = parser.parseFromString(xmlString, "text/xml");

    if (formDoc == null)
        throw new Error("Loading of form xml failed!");

    const workflowElement = formDoc.children[0];

    const sets = await getSets(workflowElement); // Načtení sad
    const definitionParticipants = getDefinitionParticipants(workflowElement); // Načtení definičních hráčů
    const activities = getActivities(workflowElement, sets, definitionParticipants); // Načtení aktivit

    const version = workflowElement.getAttribute(CONST_WORKFLOW_VERSION_AT) ?? "";
    const definitionVersion = workflowElement.getAttribute(CONST_WORKFLOW_DEFINITION_VERSION_AT) ?? "";
    const identifier = workflowElement.getAttribute(CONST_WORKFLOW_IDENTIFIER_AT) ?? "";
    const className = workflowElement.getAttribute(CONST_WORKFLOW_CLASS_NAME_AT) ?? "";

    return new Workflow(version, definitionVersion, identifier, className, sets, activities, definitionParticipants);
};

const getSets = async (workflowElement: Element): Promise<Set[]> => {
    const sets: Set[] = [];
    const setDefinitionsEl = workflowElement.getElementsByTagName(CONST_SET_DEFINITIONS_EL);

    // Pokud není žádná vytvořená sada, tak musíme aspoň jednu vytvořit (otevření nově vytvořené šablony typicky)
    if (!setDefinitionsEl.length || setDefinitionsEl[0].children.length === 0)
        return [new Set(await getNewSetLocalization(), "", 0)];

    const setElements = setDefinitionsEl[0].children;

    for (let i = 0; i < setElements.length; i++) {
        const setEl = setElements[i];
        const name = setEl.getAttribute(CONST_SET_NAME_AT) ?? "";
        const description = setEl.getAttribute(CONST_SET_DESCRIPTION_AT) ?? "";
        sets.push(new Set(name, description, i));
    }

    return sets;
};

const getActivities = (workflowElement: Element, sets: Set[], definitionParticipants: DefinitionParticipant[]) => {
    const activities: Activity[] = [];
    const activityElements = workflowElement.getElementsByTagName(CONST_ACTIVITY_EL);

    for (let i = 0; i < activityElements.length; i++) {
        const activityEl = activityElements[i];

        const actions = getActions(activityEl);
        const positions = getPositions(activityEl, sets);
        const transitions = getTransitions(activityEl, sets);
        const triggers = getTriggers(activityEl);
        const participants = getParticipants(activityEl, definitionParticipants);

        const activityId = activityEl.getAttribute(CONST_ACTIVITY_ID_AT) ?? "";
        const activityType = getActivityType(activityEl.getAttribute(CONST_ACTIVITY_TYPE_AT));
        const activity = new Activity(activityId, activityType, actions, participants, triggers, transitions, positions);
        activity.name = activityEl.getAttribute(CONST_ACTIVITY_NAME_AT) ?? "";
        activity.order = Number(activityEl.getAttribute(CONST_ACTIVITY_ORDER_AT));
        activity.condition = activityEl.getAttribute(CONST_ACTIVITY_CONDITION_AT) ?? "";
        activity.runCondition = activityEl.getAttribute(CONST_ACTIVITY_RUN_CONDITION_AT) ?? "";
        activity.description = activityEl.getAttribute(CONST_ACTIVITY_DESCRIPTION_AT) ?? "";
        activity.inheritParticipants = parseStringToBoolean(activityEl.getAttribute(CONST_ACTIVITY_INHERIT_PARTICIPANTS_AT), activity.inheritParticipants);
        activity.killChildren = parseStringToBoolean(activityEl.getAttribute(CONST_ACTIVITY_KILL_CHILDREN_AT), false);
        activity.inputTransType = getTransType(activityEl.getAttribute(CONST_ACTIVITY_INPUT_TRANS_TYPE_AT), IOTransitionType.xor);
        activity.showInWfHistory = parseStringToBoolean(activityEl.getAttribute(CONST_ACTIVITY_SHOW_IN_WF_HISTORY_AT), activity.showInWfHistory);

        if (activityId)
            activities.push(activity);
    }

    return activities;
};

const getDefinitionParticipants = (workflowElement: Element) => {
    const definitionParticipants: DefinitionParticipant[] = [];
    const definitionParticipantsEl = workflowElement.getElementsByTagName(CONST_DEFINITION_PARTICIPANTS_EL);

    if (definitionParticipantsEl.length === 0)
        return definitionParticipants;

    const { children } = definitionParticipantsEl[0];

    for (let i = 0; i < children.length; i++) {
        const definitionParticipantEl = children[i];
        const name = definitionParticipantEl.getAttribute(CONST_DEFINITION_PARTICIPANT_NAME_AT) ?? "";
        const condition = definitionParticipantEl.getAttribute(CONST_DEFINITION_PARTICIPANT_CONDITION_AT) ?? "";
        const roleName = definitionParticipantEl.getAttribute(CONST_DEFINITION_PARTICIPANT_ROLE_NAME_AT) ?? "";
        definitionParticipants.push(new DefinitionParticipant(generateUUID(), name, condition, roleName));
    }

    return definitionParticipants;
};

const getActions = (element: Element) => {
    const actions: Action[] = [];
    const actionsElements = element.getElementsByTagName(CONST_ACTIONS_EL);

    if (actionsElements.length === 0)
        return actions;

    const { children } = actionsElements[0];

    for (let i = 0; i < children.length; i++) {
        const actionEl = children[i];

        switch (actionEl.localName) {
            case CONST_CALL_METHOD_EL: {
                actions.push(new CallMethod(
                    actionEl.getAttribute(CONST_CALL_METHOD_METHOD_NAME_AT) ?? "",
                    actionEl.getAttribute(CONST_CALL_METHOD_STATIC_AT) ?? "",
                    actionEl.getAttribute(CONST_CALL_METHOD_INSERT_ACTUAL_OBJECT_AT) ?? "",
                    actionEl.getAttribute(CONST_CALL_METHOD_INSERT_PARAM_FORM_OBJECT_AT) ?? "",
                    actionEl.getAttribute(CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_CONDITION_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_COMMENT_AT) ?? ""));
                break;
            }
            case CONST_CALL_SCRIPT_EL: {
                actions.push(new CallScript(
                    unescapeXmlString(actionEl.innerHTML ?? ""),
                    actionEl.getAttribute(CONST_ACTION_CONDITION_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_COMMENT_AT) ?? ""));
                break;
            }
            case CONST_INCREMENT_DATE_TIME_EL: {
                actions.push(new IncrementDateTime(
                    actionEl.getAttribute(CONST_INCREMENT_DATE_TIME_ATTR_NAME_AT) ?? "",
                    actionEl.getAttribute(CONST_INCREMENT_DATE_TIME_PRECISION_AT) ?? "",
                    actionEl.getAttribute(CONST_INCREMENT_DATE_TIME_VALUE_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_CONDITION_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_COMMENT_AT) ?? ""));
                break;
            }
            case CONST_INCREMENT_NUMBER_EL: {
                actions.push(new IncrementNumber(
                    actionEl.getAttribute(CONST_INCREMENT_NUMBER_ATTR_NAME_AT) ?? "",
                    actionEl.getAttribute(CONST_INCREMENT_NUMBER_VALUE_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_CONDITION_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_COMMENT_AT) ?? ""));
                break;
            }
            case CONST_MESSAGE_RECIPIENT_EL: {
                // Stará akce, která už je obsolete i na SL. Nemilosrdně vyhazujeme výjimku!
                throw new Error("Action \"Message Recipient\" is not supported in new version of WF Editor!");
            }
            case CONST_SEND_CALENDAR_EL: {
                // Stará akce, která už je obsolete i na SL. Nemilosrdně vyhazujeme výjimku!
                throw new Error("Action \"Send Calendar\" is not supported in new version of WF Editor!");
            }
            case CONST_SEND_MAIL_EL: {
                manageSendMailXmlElement(actionEl, actions);
                break;
            }
            case CONST_SEND_SMS_EL: {
                manageSendSmsXmlElement(actionEl, actions);
                break;
            }
            case CONST_SEND_WF_MESSAGE_EL: {
                actions.push(new SendWFMessage(
                    actionEl.getAttribute(CONST_SEND_WF_MESSAGE_MESSAGE_NAME_AT) ?? "",
                    actionEl.getAttribute(CONST_SEND_WF_MESSAGE_TARGET_OBJECT_NAME_AT) ?? "",
                    actionEl.getAttribute(CONST_SEND_WF_MESSAGE_OBJECTS_CONDITION_AT) ?? "",
                    actionEl.getAttribute(CONST_SEND_WF_MESSAGE_OBJECTS_CONDITION_PARAMS_AT) ?? "",
                    actionEl.getAttribute(CONST_SEND_WF_MESSAGE_AFTER_SAVE_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_CONDITION_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_COMMENT_AT) ?? ""));
                break;
            }
            case CONST_SET_ATTRIBUTE_EL: {
                manageSetAttributeXmlElement(actionEl, actions);
                break;
            }
            case CONST_SET_OBJECT_ATTRIBUTE_EL: {
                actions.push(new SetObjectAttribute(
                    actionEl.getAttribute(CONST_SET_ATTRIBUTE_ATTR_NAME_AT) ?? "",
                    unescapeXmlString(actionEl.children[0]?.innerHTML ?? ""),
                    actionEl.getAttribute(CONST_SET_OBJECT_ATTRIBUTE_MASTER_CLASS_NAME_AT) ?? "",
                    actionEl.getAttribute(CONST_SET_OBJECT_ATTRIBUTE_MASTER_ATTR_NAME_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_CONDITION_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_COMMENT_AT) ?? ""));
                break;
            }
            case CONST_SET_VALUE_EL: {
                actions.push(new SetValue(
                    actionEl.getAttribute(CONST_SET_VALUE_NAME_AT) ?? "",
                    unescapeXmlString(actionEl.children[0]?.innerHTML ?? ""),
                    actionEl.getAttribute(CONST_ACTION_CONDITION_AT) ?? "",
                    actionEl.getAttribute(CONST_ACTION_COMMENT_AT) ?? ""));
                break;
            }
            default: {
                throw new Error(`Action of name '${actionEl.localName}' is not valid WF action! Please check spelling!`);
            }
        }
    }

    return actions;
};

const getTransitions = (activity: Element, sets: Set[]) => {
    const transitions: Transition[] = [];
    const transitionsElements = activity.getElementsByTagName(CONST_TRANSITIONS_EL);

    if (transitionsElements.length === 0)
        return transitions;

    const { children } = transitionsElements[0];

    for (let i = 0; i < children.length; i++) {
        const transitionEl = children[i];
        const drawElement = transitionEl.getElementsByTagName(CONST_DRAW_EL)[0];
        const pointElements = drawElement.children;
        const points: Point[] = [];

        for (let j = 0; j < pointElements.length; j++) {
            points.push(new Point(Number(pointElements[j].getAttribute(CONST_POINT_X_AT)), Number(pointElements[j].getAttribute(CONST_POINT_Y_AT))));
        }

        const setIndex = Number(drawElement.getAttribute(CONST_DRAW_SET_AT));

        if (isNaN(setIndex))
            throw new Error("Draw element doesn't have an element 'set' with set's index!'");

        const transitionId = transitionEl.getAttribute(CONST_TRANSITION_ID_AT) ?? "";
        const to = transitionEl.getAttribute(CONST_TRANSITION_TO_AT) ?? "";
        const actions = getActions(transitionEl);

        const newTransition = new Transition(transitionId, to, points, sets[setIndex].id, actions);
        newTransition.name = transitionEl.getAttribute(CONST_TRANSITION_NAME_AT) ?? "";
        newTransition.condition = transitionEl.getAttribute(CONST_TRANSITION_CONDITION_AT) ?? "";

        transitions.push(newTransition);
    }

    return transitions;
};

const getPositions = (activity: Element, sets: Set[]) => {
    const positions: Position[] = [];
    const positionsElements = activity.getElementsByTagName(CONST_POSITION_EL);

    if (positionsElements.length === 0)
        return positions;

    const { children } = positionsElements[0];

    for (let i = 0; i < children.length; i++) {
        const positionEl = children[i];
        const x = Number(positionEl.getAttribute(CONST_POSITION_X_AT));
        const y = Number(positionEl.getAttribute(CONST_POSITION_Y_AT));
        const width = Number(positionEl.getAttribute(CONST_POSITION_WIDTH_AT));
        const height = Number(positionEl.getAttribute(CONST_POSITION_HEIGHT_AT));
        const setIndex = Number(positionEl.getAttribute(CONST_POSITION_SET_AT));

        if (sets[setIndex]) {
            positions.push(new Position(x, y, width, height, sets[setIndex]));
        }
    }

    return positions;
};

const getParticipants = (activity: Element, definitionParticipants: DefinitionParticipant[]) => {
    const participants: Participant[] = [];
    const participantsElements = activity.getElementsByTagName(CONST_PARTICIPANTS_EL);

    if (participantsElements.length === 0)
        return participants;

    const { children } = participantsElements[0];

    for (let i = 0; i < children.length; i++) {
        const participantEl = children[i];

        switch (participantEl.localName) {
            case CONST_DEFINITION_PARTICIPANT_EL: {
                const foundDefParticipant = definitionParticipants.find(defParticipant => defParticipant.name === participantEl.getAttribute(CONST_DEFINITION_PARTICIPANT_NAME_AT));
                if (!foundDefParticipant)
                    throw new Error(`Could not found definition participant of the name '${participantEl.getAttribute(CONST_DEFINITION_PARTICIPANT_NAME_AT)}'`);
                participants.push(foundDefParticipant);
                break;
            }
            case CONST_OWNER_EL: {
                participants.push(new Owner());
                break;
            }
            case CONST_ROLE_EL: {
                participants.push(new Role(
                    participantEl.getAttribute(CONST_ROLE_DB_ID_AT) ?? "",
                    participantEl.getAttribute(CONST_ROLE_NAME_AT) ?? ""));
                break;
            }
            case CONST_SYSTEM_EL: {
                participants.push(new System());
                break;
            }
            case CONST_USER_EL: {
                participants.push(new User(
                    participantEl.getAttribute(CONST_USER_DB_ID_AT) ?? "",
                    participantEl.getAttribute(CONST_USER_NAME_AT) ?? ""));
                break;
            }
            default: {
                throw new Error(`Participant of given name '${participantEl.localName}' is not a valid participant! Please check spelling!`);
            }
        }
    }

    return participants;
};

const getTriggers = (activity: Element) => {
    const triggers: Trigger[] = [];
    const triggersElements = activity.getElementsByTagName(CONST_TRIGGERS_EL);

    if (triggersElements.length === 0)
        return triggers;

    const { children } = triggersElements[0];

    for (let i = 0; i < children.length; i++) {
        const triggerEl = children[i];

        switch (triggerEl.localName) {
            case CONST_ATTRIBUTE_DATA_CHANGE_EL: {
                triggers.push(new AttributeDataChange(
                    triggerEl.getAttribute(CONST_ATTRIBUTE_DATA_CHANGE_ATTR_NAME_AT) ?? "",
                    triggerEl.getAttribute(CONST_ATTRIBUTE_DATA_CHANGE_CONDITION_AT) ?? ""));
                break;
            }
            case CONST_MENU_EL: {
                const captions = getCaptions(triggerEl);
                triggers.push(new Menu(
                    triggerEl.getAttribute(CONST_MENU_IDENTIFIER_AT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_CODE_NAME) ?? "",
                    triggerEl.getAttribute(CONST_MENU_ICON_NAME) ?? "",
                    triggerEl.getAttribute(CONST_MENU_PARTICIPANTS_SELECTING_AT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_MULTI_ACTION_ENABLE_AT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_VALIDATE_POST_REQ_AT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_VALIDATE_RECOMMENDED_AT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_CHOOSE_FROM_USERS_DEFAULT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_MASTER_NODE_AT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_PARAM_FORM_NAME_AT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_LINK_ATTR_FOR_SEL_OBJ_AT) ?? "",
                    triggerEl.getAttribute(CONST_MENU_SET_VALUES_AT) ?? "",
                    captions,
                    getConfirmMessages(triggerEl, captions)));
                break;
            }
            case CONST_NEW_OBJECT_EL: {
                triggers.push(new NewObject());
                break;
            }
            case CONST_WFMESSAGE_EL: {
                triggers.push(new WfMessage(
                    triggerEl.getAttribute(CONST_WFMESSAGE_MESSAGE_NAME_AT) ?? "",
                    triggerEl.getAttribute(CONST_WFMESSAGE_DEACTIVATING_AT) ?? ""));
                break;
            }
            default: {
                throw new Error(`Trigger of given name '${triggerEl.localName}' is not a valid trigger! Please check spelling!`);
            }
        }
    }

    return triggers;
};

const getTransType = (attributeValue: string | null, defaultIoTransType: IOTransitionType): IOTransitionType => {
    return attributeValue === IOTransitionType.and ? IOTransitionType.and : defaultIoTransType;
};

const getActivityType = (attributeValue: string | null): ActivityType => {
    switch (attributeValue) {
        case "normal": {
            return ActivityType.normal;
        }
        case "start": {
            return ActivityType.start;
        }
        case "end": {
            return ActivityType.end;
        }
        default: {
            throw new Error(`Given Activity Type '${attributeValue}' is not supported. Cannot parse workflow!`);
        }
    }
};

const getCaptions = (triggerEl: Element) => {
    const captions: Text[] = [];
    const captionsEl = triggerEl.querySelector(CONST_MENU_CAPTIONS_EL);

    if (captionsEl == null)
        throw new Error(`XML element "${CONST_MENU_CAPTIONS_EL}" is missing for trigger of type "Menu"! \n ${triggerEl}`);

    for (let i = 0; i < captionsEl.children.length; i++) {
        captions.push(new Text(
            captionsEl.children[i].getAttribute(CONST_MENU_LANG_AT) ?? "",
            captionsEl.children[i].getAttribute(CONST_MENU_TEXT_AT) ?? ""));
    }

    return captions;
};

const getConfirmMessages = (triggerEl: Element, captions: Text[]) => {
    const confirmMessages: Text[] = [];
    const confirmMessagesEl = triggerEl.querySelector(CONST_MENU_CONFIRM_MESSAGES_EL);

    if (confirmMessagesEl == null)
        throw new Error(`XML element "${CONST_MENU_CONFIRM_MESSAGES_EL}" is missing for trigger of type "Menu"! \n ${triggerEl}`);

    for (let i = 0; i < captions.length; i++) {
        const confirmMessage = findAdequateConfirmMsg(captions[i], confirmMessagesEl);
        confirmMessages.push(confirmMessage ?? new Text(captions[i].lang, ""));
    }

    return confirmMessages;
};

/**
 * Vrací lokalizovaný název pro nově vytvořenou sadu.
 */
const getNewSetLocalization = async () => {
    const locItems = await loadLocalizationItems([LOC_NEW_SET]);
    return locItems.get(LOC_NEW_SET)?.primaryText ?? "Nová sada";
};

/**
 * Pokud nenajde confirm message pro související(podle jazyka) caption, vrátí undefined,
 * v opačném případě ponechá původní hodnoty
 */
const findAdequateConfirmMsg = (currentCaption: Text, confirmMessagesEl: Element) => {
    for (const confirmMessageChild of confirmMessagesEl.children) {
        if (confirmMessageChild.getAttribute(CONST_MENU_LANG_AT) === currentCaption.lang) {
            return new Text(
                confirmMessageChild.getAttribute(CONST_MENU_LANG_AT) ?? "",
                confirmMessageChild.getAttribute(CONST_MENU_TEXT_AT) ?? "");
        }
    }
    return undefined;
};
